import { Search, mq, GhostButton } from "@certn/cascadia-react";
import { css } from "@emotion/css";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useQueryParams } from "../../hooks/useQueryParams";

interface Props {
  testID: string;
  hasSearch?: boolean;
  searchPlaceholder?: string;
  searchMinLength?: number;
  clearFilterExceptions?: string[];
  children?: React.ReactNode;
}

export function FilterBar(props: Props) {
  const {
    children,
    testID,
    hasSearch,
    searchPlaceholder,
    searchMinLength = 0,
    clearFilterExceptions,
  } = props;
  const { clearParam, params, setParam } = useQueryParams();
  const { t } = useTranslation();
  const searchParam = params["search"] ?? "";
  const [draftSearch, setDraftSearch] = useState(searchParam);

  useEffect(() => {
    setDraftSearch(searchParam);
  }, [searchParam]);

  const onChange = useCallback(
    (value: string) => {
      setDraftSearch(value);
      const trimmedValue = value.trim();
      if (trimmedValue.length === 0) {
        clearParam("search");
        return;
      }
      if (trimmedValue.length >= searchMinLength) {
        setParam("search", trimmedValue);
        return;
      }
      if (searchParam) {
        clearParam("search");
      }
    },
    [clearParam, searchMinLength, searchParam, setParam]
  );
  const canClearParam = (param: string) =>
    param !== "page-count" &&
    (!clearFilterExceptions || !clearFilterExceptions.includes(param));

  return (
    <div className={styles.container}>
      {children && (
        <div className={styles.filters}>
          {children}
          {Object.keys(params).length > 0 && (
            <GhostButton
              size="small"
              testID="clear-filters"
              onPress={() => {
                for (const param in params) {
                  if (canClearParam(param)) {
                    clearParam(param);
                  }
                }
              }}
            >
              {t("Clear filters")}
            </GhostButton>
          )}
        </div>
      )}

      {hasSearch && (
        <Search
          value={draftSearch}
          testID={`${testID}-list-search`}
          aria-label={searchPlaceholder ?? t("Search")}
          placeholder={searchPlaceholder ?? t("Search")}
          className={styles.search}
          onChange={onChange}
          handleChange={() => {
            clearParam("page");
          }}
        />
      )}
    </div>
  );
}

const styles = {
  container: css({
    display: "flex",
    justifyContent: "space-between",
    flexWrap: "wrap",
    rowGap: "var(--space-3)",
    columnGap: "var(--space-2)",
    flexShrink: 1,
  }),
  filters: css({
    display: "flex",
    rowGap: "var(--space-3)",
    columnGap: "var(--space-2)",
    flexWrap: "wrap",
    flexShrink: 1,
    flexGrow: 1,
    alignItems: "flex-start",
  }),
  search: css(
    {
      display: "flex",
      flexShrink: 1,
      justifyContent: "flex-end",
    },
    mq({
      width: ["100%", "auto"],
      maxWidth: ["auto", 276],
      flexBasis: ["auto", 276],
    })
  ),
};
